import { useMemo } from 'react';
import { Box, Tooltip, useTheme } from '@mui/material';
import { computeTableGeometry, seatLabel } from '../../../lib/seatingGeometry';
import { attendanceNote, occupantName } from '../../../lib/seatingCandidates';
import type { SeatAssignment, SeatingTable } from '../../../types/seating';

/**
 * The drawing itself: a table as the shape it actually is, chairs around it.
 *
 * Inline SVG rather than a canvas, and every chair is a real focusable control —
 * the room has to be readable, printable and reachable by a finger. Nothing is
 * dragged. The operator's flow is tap a chair, pick a guest, because drag is
 * unusable on the phone a host is holding in the venue and a drag-only chart is
 * one nobody can fix standing at the table.
 *
 * Shared by the room and by the add-a-table preview, so the shape a host picks
 * from a menu is drawn by the same code that will draw it for real.
 *
 * Positions are recomputed on every render and never cached: growing a side
 * renumbers every later ordinal, so a cached chair would draw the room one seat
 * out of step with who is sitting in it.
 */

/**
 * A name over two lines: given name, then everything else.
 *
 * Chairs on a long table sit about 45cm apart and a full name on one line is
 * wider than that, so it would run into the neighbour. Splitting at the first
 * space is the break a reader expects, and it keeps the wider line — the
 * surname, or a double-barrelled one — on its own.
 *
 * "Jess's +1" and other guest labels have no surname to split off and stay on
 * one line, which is correct: they are already short.
 */
export function splitNameForChart(name: string): string[] {
  const parts = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return parts;
  return [parts[0], parts.slice(1).join(' ')];
}

/** Two letters is what fits in a chair; the full name is drawn beside it. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
  return (first + last).toUpperCase();
}

export default function TableCanvas({
  table,
  seatPitchCm,
  assignments,
  placing,
  onSeatClick,
  ariaLabel,
}: {
  /** Only shape and the per-side counts are read; a draft table works. */
  table: Partial<SeatingTable>;
  seatPitchCm: number;
  assignments?: Map<number, SeatAssignment>;
  /** Who the next tap seats, when the host has armed a placement or a move. */
  placing?: string | null;
  /** Omitted for a preview: the chairs then draw but do not invite a tap. */
  onSeatClick?: (seatIndex: number, assignment: SeatAssignment | null) => void;
  ariaLabel: string;
}) {
  const theme = useTheme();
  const geometry = useMemo(() => computeTableGeometry(table, seatPitchCm), [table, seatPitchCm]);

  /*
    Names, not initials — and outside the chair, because they do not fit inside
    one.

    A chair is about 45cm wide and "Maria Teresa Gonçalves" is not. Initials
    inside the circle was the compromise, and it fails at the only moment the
    chart is worth anything: handing it to a planner, a maitre d', or the person
    writing place cards. "S" is not a person. So the circle keeps the short
    glyph for scanning, and the full name sits beside it for reading.

    `showNames` is off for the little preview in the add-table dialog, where
    there are no occupants to name anyway.
  */
  const showNames = Boolean(assignments && assignments.size > 0);
  const nameSize = geometry.seatRadius * 0.46;
  /*
    How far past the outermost chair a name reaches: the gap to the chair
    (1.5r), the second line (1.1 × nameSize), and one more line's worth for the
    ascender and descender the baseline does not account for — less the chair's
    own radius, which `extent` already covers. Guessed too small, the surname
    is cropped by the edge of the picture rather than by a circle, which looks
    identical to the bug this replaced.
  */
  const nameBand = showNames
    ? geometry.seatRadius * 1.5 + nameSize * 1.1 + nameSize - geometry.seatRadius
    : 0;

  // Padding keeps a chair's stroke inside the viewBox instead of clipped, and
  // the name band keeps the longest name from being cropped by the edge.
  const pad = geometry.seatRadius * 0.6;
  const width = geometry.extent.width + (pad + nameBand * 1.6) * 2;
  const height = geometry.extent.height + (pad + nameBand) * 2;

  const seatFill = (assignment: SeatAssignment | null) => {
    if (!assignment) return theme.palette.background.paper;
    if (assignment.seatKind === 'blocked') return theme.palette.action.disabledBackground;
    if (assignment.seatKind === 'guest') return theme.palette.secondary.light;
    return theme.palette.primary.main;
  };
  const seatStroke = (assignment: SeatAssignment | null) => {
    // A warning ring is the whole point of the stale flag: someone is sitting in
    // a chair at a dinner they no longer hold a spot at, and only the host gets
    // to decide whether that is wrong.
    if (assignment && attendanceNote(assignment.attendanceStatus)) return theme.palette.warning.main;
    if (!assignment) return theme.palette.divider;
    return theme.palette.primary.dark;
  };

  return (
    <Box
      component="svg"
      viewBox={`${-width / 2} ${-height / 2} ${width} ${height}`}
      role="group"
      aria-label={ariaLabel}
      sx={{ width: '100%', height: 'auto', display: 'block', touchAction: 'manipulation' }}
    >
      {geometry.body.kind === 'circle' ? (
        <circle
          cx={0}
          cy={0}
          r={geometry.body.radius}
          fill={theme.palette.action.hover}
          stroke={theme.palette.divider}
          strokeWidth={geometry.seatRadius * 0.12}
        />
      ) : (
        <rect
          x={-geometry.body.width / 2}
          y={-geometry.body.height / 2}
          width={geometry.body.width}
          height={geometry.body.height}
          rx={geometry.seatRadius * 0.5}
          fill={theme.palette.action.hover}
          stroke={theme.palette.divider}
          strokeWidth={geometry.seatRadius * 0.12}
        />
      )}

      {geometry.seats.map((seat) => {
        const assignment = assignments?.get(seat.seatIndex) ?? null;
        const name = assignment ? occupantName(assignment) : '';
        const stale = assignment ? attendanceNote(assignment.attendanceStatus) : '';
        const label = assignment
          ? `${seatLabel(geometry, seat.seatIndex)} — ${name}${stale ? ` (${stale})` : ''}`
          : `${seatLabel(geometry, seat.seatIndex)} — empty${placing ? `, seat ${placing} here` : ''}`;
        const glyph = assignment
          ? assignment.seatKind === 'blocked'
            ? '×'
            : initials(name)
          : String(seat.seatIndex + 1);

        const chair = (
          <>
            {/* Invisible, and larger than the chair: a 17cm chair on a phone is
                under the 44px anyone can reliably hit. */}
            {onSeatClick ? (
              <circle cx={seat.x} cy={seat.y} r={geometry.seatRadius * 1.45} fill="transparent" />
            ) : null}
            <circle
              cx={seat.x}
              cy={seat.y}
              r={geometry.seatRadius}
              fill={seatFill(assignment)}
              stroke={seatStroke(assignment)}
              strokeWidth={geometry.seatRadius * (assignment ? 0.16 : 0.1)}
              strokeDasharray={assignment ? undefined : `${geometry.seatRadius * 0.35}`}
            />
            <text
              x={seat.x}
              y={seat.y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={geometry.seatRadius * (assignment ? 0.85 : 0.7)}
              fontWeight={700}
              fill={
                assignment && assignment.seatKind === 'invitee'
                  ? theme.palette.primary.contrastText
                  : theme.palette.text.secondary
              }
            >
              {glyph}
            </text>

            {/*
              The name reads outward, on the side of the chair facing away from
              the table, so it never crosses the tabletop or a neighbour. Two
              lines keeps each one short enough not to collide with the chair
              next door — a long table puts chairs ~45cm apart and full names
              are wider than that on one line.
            */}
            {showNames && name && assignment?.seatKind !== 'blocked'
              ? (() => {
                  /*
                    A text block grows DOWNWARD from its first line, so the two
                    sides are not mirror images of each other. Below the chair,
                    the first line is the one that clears it. Above the chair,
                    the LAST line is — so the block has to start higher by every
                    line after the first, or the surname walks back down into the
                    circle and gets covered by it. That is what the top row was
                    doing: names drawn, half of each one behind a chair.
                  */
                  const lines = splitNameForChart(name);
                  const [first, ...rest] = lines;
                  const gap = geometry.seatRadius * 1.5;
                  const top = seat.y >= 0
                    ? seat.y + gap + nameSize * 0.9
                    : seat.y - gap - nameSize * 1.1 * (lines.length - 1);
                  return (
                    <text
                      x={seat.x}
                      y={top}
                      textAnchor="middle"
                      fontSize={nameSize}
                      fontWeight={600}
                      fill={theme.palette.text.primary}
                      style={{ pointerEvents: 'none' }}
                    >
                      <tspan x={seat.x}>{first}</tspan>
                      {rest.length ? (
                        <tspan x={seat.x} dy={nameSize * 1.1}>{rest.join(' ')}</tspan>
                      ) : null}
                    </text>
                  );
                })()
              : null}
          </>
        );

        if (!onSeatClick) {
          return (
            <g key={seat.seatIndex} aria-label={label}>
              {chair}
            </g>
          );
        }

        return (
          <Tooltip key={seat.seatIndex} title={label} enterTouchDelay={400}>
            <g
              role="button"
              tabIndex={0}
              aria-label={label}
              style={{ cursor: 'pointer' }}
              onClick={() => onSeatClick(seat.seatIndex, assignment)}
              onKeyDown={(event) => {
                // Keyboard parity is not decoration here: the same host who
                // seats 137 people on a phone does the first pass at a desk.
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSeatClick(seat.seatIndex, assignment);
                }
              }}
            >
              {chair}
            </g>
          </Tooltip>
        );
      })}
    </Box>
  );
}
